import {useLayoutEffect, useRef, useState} from 'react';
import {
    calcMatAndMarkup,
    Ki,
    Cursor,
    addMoveToCurrentNode,
    zeros,
    empty,
    SGF_LETTERS,
    TNode,
    extractBoardSize,
    Markup,
    Theme,
} from 'ghostban';
import {Board, type BoardRefType} from './Board';
import {
    getFirstSetupNodeFromSgf,
    getFirstToMoveColor,
    GHOSTBAN_OPTIONS,
} from '@utils';

const MOVE_INTERVAL_MS = 300;

type MoveFrame = {
    mat: number[][];
    markup: string[][];
    visibleAreaMat: number[][];
};

function computeFrames(
    sgf: string,
    compressedPath?: string | null
): {frames: MoveFrame[]; boardSize: number} {
    const setupNode = getFirstSetupNodeFromSgf(sgf);
    if (!setupNode) return {frames: [], boardSize: 19};

    const size = extractBoardSize(setupNode);
    const frames: MoveFrame[] = [];

    const initialCalc = calcMatAndMarkup(setupNode);
    const initVisibleArea = initialCalc.visibleAreaMat;
    frames.push({
        mat: initialCalc.mat,
        markup: initialCalc.markup,
        visibleAreaMat: initVisibleArea,
    });

    if (!compressedPath) return {frames, boardSize: size};

    const pathPairs = compressedPath.match(/.{1,2}/g) || [];
    if (pathPairs.length === 0) return {frames, boardSize: size};

    let turn = getFirstToMoveColor(sgf);
    let currentNode: TNode = setupNode;

    for (const pair of pathPairs) {
        const i = SGF_LETTERS.indexOf(pair[0].toLowerCase());
        const j = SGF_LETTERS.indexOf(pair[1].toLowerCase());

        const {mat: currentMat} = calcMatAndMarkup(currentNode);
        const node = addMoveToCurrentNode(currentNode, currentMat, i, j, turn);

        if (!node) break;

        const calc = calcMatAndMarkup(node);
        const newMarkup = calc.markup.map(row => [...row]);
        newMarkup[i][j] = Markup.Current;

        frames.push({
            mat: calc.mat,
            markup: newMarkup,
            visibleAreaMat: initVisibleArea,
        });

        turn = turn === Ki.Black ? Ki.White : Ki.Black;
        currentNode = node;
    }

    return {frames, boardSize: size};
}

type MoveReplayBoardProps = {
    sgf: string;
    compressedPath?: string | null;
    onComplete?: () => void;
    className?: string;
};

export const MoveReplayBoard = ({
    sgf,
    compressedPath,
    onComplete,
    className,
}: MoveReplayBoardProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const boardRef = useRef<BoardRefType>(null);
    const [boardPixelSize, setBoardPixelSize] = useState(0);
    const [mat, setMat] = useState<number[][]>(zeros([19, 19]));
    const [markup, setMarkup] = useState<string[][]>(empty([19, 19]));
    const [visibleAreaMat, setVisibleAreaMat] = useState<number[][]>(
        zeros([19, 19])
    );
    const [boardSize, setBoardSize] = useState(19);

    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const updateSize = () => setBoardPixelSize(container.clientWidth);
        updateSize();
        const observer = new ResizeObserver(updateSize);
        observer.observe(container);
        return () => observer.disconnect();
    }, []);

    useLayoutEffect(() => {
        boardRef.current?.getBoard()?.resize();
    }, [boardPixelSize]);

    useLayoutEffect(() => {
        const {frames, boardSize: size} = computeFrames(sgf, compressedPath);
        setBoardSize(size);

        if (frames.length === 0) {
            if (compressedPath) onCompleteRef.current?.();
            return;
        }

        // Show initial position
        const first = frames[0];
        setMat(first.mat);
        setMarkup(first.markup);
        setVisibleAreaMat(first.visibleAreaMat);

        if (!compressedPath) return;

        if (frames.length <= 1) {
            onCompleteRef.current?.();
            return;
        }

        let step = 0;
        const timer = setInterval(() => {
            step += 1;
            if (step >= frames.length) {
                clearInterval(timer);
                const last = frames[frames.length - 1];
                setMat(last.mat);
                setMarkup(last.markup);
                setVisibleAreaMat(last.visibleAreaMat);
                onCompleteRef.current?.();
                return;
            }
            const frame = frames[step];
            setMat(frame.mat);
            setMarkup(frame.markup);
            setVisibleAreaMat(frame.visibleAreaMat);
        }, MOVE_INTERVAL_MS);

        return () => clearInterval(timer);
    }, [sgf, compressedPath]);

    return (
        <div
            ref={containerRef}
            className={className}
            style={{aspectRatio: '1 / 1'}}>
            {boardPixelSize > 0 && (
                <Board
                    ref={boardRef}
                    boardOptions={{
                        ...GHOSTBAN_OPTIONS,
                        interactive: false,
                        zoom: true,
                        boardSize: boardSize,
                        coordinate: false,
                        theme: Theme.Flat,
                    }}
                    size={boardPixelSize}
                    mat={mat}
                    markup={markup}
                    visibleAreaMat={visibleAreaMat}
                    cursor={Cursor.None}
                    turn={Ki.Empty}
                    disabled={true}
                />
            )}
        </div>
    );
};
